import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, ShieldCheck, CircleCheckBig, MessageCircle, RotateCcw } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  DESIRED_PACKAGES,
  CURRENT_PACKAGES,
  BILL_PAYMENT_METHODS,
  ORDER_REQUIREMENTS,
  WHATSAPP_NUMBER,
} from "../constants/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildWaMessage = (f) =>
  `Halo RismaCell, saya mau order migrasi paket retensi:

No IndiHome: ${f.indihome_number}
Nama pemilik: ${f.owner_name}
NIK KTP: ${f.nik_ktp}
Tempat & Tanggal Lahir: ${f.birth_place_date}
Nominal tagihan terakhir: Rp ${f.last_bill_amount}
Sudah bayar tagihan bulan ini: ${f.bill_paid_this_month}
Bayar tagihan lewat: ${f.bill_payment_method}

Alamat pemasangan:
Jalan: ${f.address_street}
Desa/Kel: ${f.address_village}
Kecamatan: ${f.address_district}
Kota/Kabupaten: ${f.address_city}
Provinsi: ${f.address_province}

Email aktif: ${f.email}
No HP aktif: ${f.phone}
Paket saat ini: ${f.current_package}
Berlangganan min. 6 bulan: ${f.min_6_months}
Add-on Kuota Keluarga: ${f.addon_kuota_keluarga}
Paket yang dituju: ${f.desired_package}
Catatan: ${f.notes || "-"}`;

const INITIAL = {
  indihome_number: "",
  owner_name: "",
  nik_ktp: "",
  birth_place_date: "",
  last_bill_amount: "",
  bill_paid_this_month: "",
  bill_payment_method: "",
  address_street: "",
  address_village: "",
  address_district: "",
  address_city: "",
  address_province: "",
  email: "",
  phone: "",
  current_package: "",
  min_6_months: "",
  addon_kuota_keluarga: "",
  desired_package: "",
  notes: "",
};

const REQUIRED = [
  "indihome_number", "owner_name", "nik_ktp", "birth_place_date",
  "last_bill_amount", "bill_paid_this_month", "bill_payment_method",
  "address_street", "address_village", "address_district", "address_city",
  "address_province", "email", "phone", "current_package", "min_6_months",
  "addon_kuota_keluarga", "desired_package",
];

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <Label>{label}</Label>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="font-heading text-base font-bold text-gray-900 pt-4 border-t border-gray-100 first:border-0 first:pt-0">
    {children}
  </h3>
);

export const OrderForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = REQUIRED.filter((k) => !form[k]?.trim());
    if (missing.length) {
      toast.error("Mohon lengkapi semua kolom wajib.");
      return;
    }
    if (form.min_6_months !== "Ya" || form.bill_paid_this_month !== "Ya") {
      toast.error(
        "Order belum bisa diproses: pastikan sudah berlangganan minimal 6 bulan dan tagihan bulan ini sudah lunas."
      );
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/orders`, form);
      const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        buildWaMessage(form)
      )}`;
      setSubmitted({ waUrl, name: form.owner_name });
      window.open(waUrl, "_blank", "noopener,noreferrer");
      toast.success("Order terkirim! Form juga siap dikirim ke WhatsApp admin.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Gagal mengirim order. Coba lagi atau hubungi via WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="order"
      data-testid="order-form-section"
      className="py-20 md:py-32 bg-[#E60000] relative overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-white/5 pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-5 gap-14 items-start">
        <div className="lg:col-span-2 text-white">
          <p className="text-xs md:text-sm font-semibold uppercase tracking-widest text-white/80">
            Order Sekarang
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-4 leading-tight">
            Siap Bayar Lebih Murah Bulan Depan?
          </h2>
          <p className="text-base text-white/85 mt-5 leading-relaxed">
            Isi form lengkap di samping — data ini yang dipakai untuk pengajuan
            migrasi ke paket retensi.
          </p>

          <div
            data-testid="order-safe-payment-highlight"
            className="mt-6 bg-white rounded-2xl p-5 flex items-center gap-4"
          >
            <span className="w-11 h-11 rounded-full bg-green-600 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-white" strokeWidth={2.25} />
            </span>
            <div>
              <p className="font-heading font-extrabold text-gray-900 leading-tight">
                Bayar jasa SETELAH migrasi berhasil
              </p>
              <p className="text-xs text-gray-500 font-medium mt-0.5">
                Tanpa biaya di muka — aman untuk kamu
              </p>
            </div>
          </div>

          <div
            data-testid="order-requirements-box"
            className="mt-8 bg-white/10 border border-white/20 rounded-2xl p-6"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-white" />
              <h3 className="font-heading font-bold text-white">
                Syarat Order
              </h3>
            </div>
            <ul className="mt-4 space-y-3">
              {ORDER_REQUIREMENTS.map((r) => (
                <li key={r} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-white shrink-0 mt-1.5" />
                  <span className="text-sm font-medium text-white/90">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-3 bg-white rounded-3xl p-8 md:p-10 shadow-2xl">
          {submitted ? (
            <div
              data-testid="order-success-panel"
              className="text-center py-10 space-y-6"
            >
              <span className="inline-flex w-20 h-20 rounded-full bg-green-50 items-center justify-center">
                <CircleCheckBig className="w-10 h-10 text-green-600" />
              </span>
              <div>
                <h3 className="font-heading text-2xl font-bold text-gray-900">
                  Order Berhasil Terkirim!
                </h3>
                <p className="text-gray-600 mt-3 max-w-md mx-auto leading-relaxed">
                  Terima kasih, {submitted.name}. Order kamu sudah tercatat di
                  sistem kami. Klik tombol di bawah untuk mengirim detail form
                  langsung ke WhatsApp admin agar diproses lebih cepat.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={submitted.waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="order-success-wa-button"
                  className="inline-flex justify-center items-center gap-2 bg-[#25D366] text-white font-semibold px-8 py-4 rounded-full hover:opacity-90 active:scale-95 transition-[opacity,transform]"
                >
                  <MessageCircle className="w-5 h-5" />
                  Kirim Form ke WhatsApp Admin
                </a>
                <button
                  type="button"
                  data-testid="order-new-button"
                  onClick={() => setSubmitted(null)}
                  className="inline-flex justify-center items-center gap-2 border-2 border-gray-200 text-gray-700 font-semibold px-8 py-4 rounded-full hover:border-gray-400 active:scale-95 transition-[border-color,transform]"
                >
                  <RotateCcw className="w-5 h-5" />
                  Isi Order Baru
                </button>
              </div>
            </div>
          ) : (
        <form
          onSubmit={handleSubmit}
          data-testid="order-form"
          className="space-y-5"
        >
          <SectionTitle>Data IndiHome</SectionTitle>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="No IndiHome *">
              <Input
                data-testid="order-indihome-number-input"
                placeholder="Contoh: 122xxxxxxxx"
                inputMode="numeric"
                value={form.indihome_number}
                onChange={set("indihome_number")}
              />
            </Field>
            <Field label="Nama Pemilik *">
              <Input
                data-testid="order-owner-name-input"
                placeholder="Sesuai KTP"
                value={form.owner_name}
                onChange={set("owner_name")}
              />
            </Field>
            <Field label="NIK KTP *">
              <Input
                data-testid="order-nik-input"
                placeholder="16 digit NIK"
                inputMode="numeric"
                maxLength={16}
                value={form.nik_ktp}
                onChange={set("nik_ktp")}
              />
            </Field>
            <Field label="Tempat & Tanggal Lahir *">
              <Input
                data-testid="order-birth-input"
                placeholder="Contoh: Bandung, 17-08-1995"
                value={form.birth_place_date}
                onChange={set("birth_place_date")}
              />
            </Field>
            <Field label="Nominal Tagihan Terakhir *">
              <Input
                data-testid="order-bill-amount-input"
                placeholder="Contoh: 450000"
                inputMode="numeric"
                value={form.last_bill_amount}
                onChange={set("last_bill_amount")}
              />
            </Field>
            <Field label="Email Aktif *">
              <Input
                data-testid="order-email-input"
                type="email"
                placeholder="email@contoh.com"
                value={form.email}
                onChange={set("email")}
              />
            </Field>
            <Field label="No HP Aktif *">
              <Input
                data-testid="order-phone-input"
                placeholder="Contoh: 0812xxxxxxx"
                inputMode="tel"
                value={form.phone}
                onChange={set("phone")}
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Sudah bayar tagihan berjalan bulan ini? *">
              <RadioGroup
                data-testid="order-bill-paid-radio"
                value={form.bill_paid_this_month}
                onValueChange={set("bill_paid_this_month")}
                className="flex gap-4 pt-1"
              >
                {["Ya", "Belum"].map((v) => (
                  <div key={v} className="flex items-center gap-2">
                    <RadioGroupItem value={v} id={`paid-${v}`} />
                    <Label htmlFor={`paid-${v}`} className="font-normal">{v}</Label>
                  </div>
                ))}
              </RadioGroup>
            </Field>
            <Field label="Bayar tagihan bulan ini lewat apa? *">
              <Select
                value={form.bill_payment_method}
                onValueChange={set("bill_payment_method")}
              >
                <SelectTrigger data-testid="order-payment-method-select">
                  <SelectValue placeholder="Pilih metode" />
                </SelectTrigger>
                <SelectContent>
                  {BILL_PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <SectionTitle>Alamat Pemasangan</SectionTitle>

          <Field label="Jalan *">
            <Input
              data-testid="order-address-street-input"
              placeholder="Nama jalan, nomor rumah, RT/RW"
              value={form.address_street}
              onChange={set("address_street")}
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Desa/Kelurahan *">
              <Input
                data-testid="order-address-village-input"
                value={form.address_village}
                onChange={set("address_village")}
              />
            </Field>
            <Field label="Kecamatan *">
              <Input
                data-testid="order-address-district-input"
                value={form.address_district}
                onChange={set("address_district")}
              />
            </Field>
            <Field label="Kota/Kabupaten *">
              <Input
                data-testid="order-address-city-input"
                value={form.address_city}
                onChange={set("address_city")}
              />
            </Field>
            <Field label="Provinsi *">
              <Input
                data-testid="order-address-province-input"
                value={form.address_province}
                onChange={set("address_province")}
              />
            </Field>
          </div>

          <SectionTitle>Paket</SectionTitle>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Paket Saat Ini *">
              <Select
                value={form.current_package}
                onValueChange={set("current_package")}
              >
                <SelectTrigger data-testid="order-current-package-select">
                  <SelectValue placeholder="Pilih paket" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENT_PACKAGES.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Paket yang Dituju *">
              <Select
                value={form.desired_package}
                onValueChange={set("desired_package")}
              >
                <SelectTrigger data-testid="order-desired-package-select">
                  <SelectValue placeholder="Pilih paket retensi" />
                </SelectTrigger>
                <SelectContent>
                  {DESIRED_PACKAGES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Berlangganan minimal sudah 6 bulan? *">
              <RadioGroup
                data-testid="order-min-6-months-radio"
                value={form.min_6_months}
                onValueChange={set("min_6_months")}
                className="flex gap-4 pt-1"
              >
                {["Ya", "Belum"].map((v) => (
                  <div key={v} className="flex items-center gap-2">
                    <RadioGroupItem value={v} id={`6bln-${v}`} />
                    <Label htmlFor={`6bln-${v}`} className="font-normal">{v}</Label>
                  </div>
                ))}
              </RadioGroup>
            </Field>
            <Field label="Punya add-on Kuota Keluarga? *">
              <RadioGroup
                data-testid="order-addon-radio"
                value={form.addon_kuota_keluarga}
                onValueChange={set("addon_kuota_keluarga")}
                className="flex gap-4 pt-1"
              >
                {["Ya", "Tidak"].map((v) => (
                  <div key={v} className="flex items-center gap-2">
                    <RadioGroupItem value={v} id={`addon-${v}`} />
                    <Label htmlFor={`addon-${v}`} className="font-normal">{v}</Label>
                  </div>
                ))}
              </RadioGroup>
            </Field>
          </div>

          <Field label="Catatan (opsional)">
            <Textarea
              data-testid="order-notes-textarea"
              placeholder="Info tambahan untuk kami..."
              rows={3}
              value={form.notes}
              onChange={set("notes")}
            />
          </Field>

          <Button
            type="submit"
            data-testid="order-submit-button"
            disabled={loading}
            className="w-full bg-[#E60000] hover:bg-red-700 text-white font-semibold text-base py-6 rounded-full active:scale-[0.98] transition-[background-color,transform]"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Mengirim...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Kirim Order Migrasi
              </>
            )}
          </Button>
        </form>
          )}
        </div>
      </div>
    </section>
  );
};
